import { useEffect, useCallback, useState } from 'react'

import { appContextReducerActions, setUserCountry } from 'contexts/app-context/utils'
import { getAppInfo, logBridgeError, sendError, sendScreenView } from 'common'
import { getEntityMenuContent } from 'contexts/app-context/repository'
import { Routes } from 'common/constants'
import { useNavigate } from 'react-router-dom'

import { useAppContext } from './useAppContext'
import { getUserEntities } from '../repository/get-user-entities'
import { TEntity, TLanguage, TUserType } from '../types/app-context/components/utils'
import { IEndpointsResponse, IGetPrivacyCenterContentResponse } from '../types'
import { getPrivacyCenter } from '../repository/getPrivacyCenter'
import { hasUserContextData } from '../utils/app-context-storage'

type TValidateQuickLoad = {
  target: string | null
  entity: string | null
  language: TLanguage | null
  organization: TUserType | null
  successCallback: (
    privacyCenterComponents: IGetPrivacyCenterContentResponse<TEntity, TUserType>,
    privacyCenterEndpoints: IEndpointsResponse,
  ) => void
  errorCallback: () => void
}

export async function validateQuickLoad({
  target,
  entity,
  language,
  organization,
  successCallback,
  errorCallback,
}: TValidateQuickLoad) {
  try {
    if (target && entity && organization && language) {
      const { privacyCenterComponents, privacyCenterEndpoints } = await getPrivacyCenter({
        userAccountType: organization,
        userCountry: setUserCountry(organization),
        userLanguage: language,
        userSelectedEntity: entity as TEntity,
      })

      if (privacyCenterComponents && privacyCenterEndpoints)
        successCallback(privacyCenterComponents, privacyCenterEndpoints)
    }
  } catch {
    try {
      await sendError({
        flow: 'app_initialization',
        step: 'validate_quick_load',
        screen_name: Routes.ROOT,
        error_message: 'Quick load validation failed',
      })
    } catch (analyticsError: unknown) {
      logBridgeError('send quick load validation error', analyticsError)
    }

    errorCallback()
  }
}

const useInitializeAppContext = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isCompleted, setIsCompleted] = useState(false)

  const appContext = useAppContext()
  const { dispatch } = appContext
  const navigate = useNavigate()

  const hasInitializedContext = hasUserContextData(appContext)

  const initializeUserData = useCallback(async () => {
    setIsLoading(true)
    try {
      const { language, organization } = await getAppInfo()

      dispatch(appContextReducerActions.setLoading(true))
      dispatch(appContextReducerActions.setError(false))

      const [userEntitiesPayload, entitiesMenuInfo] = await Promise.all([
        getUserEntities(),
        getEntityMenuContent(language),
      ])

      const userOrgs = Object.keys(userEntitiesPayload.accountOrganizations)
      const entities = Object.values(userEntitiesPayload.accountOrganizations)

      if (userOrgs.length === 1) {
        if (userOrgs.includes('US') || userOrgs.includes('AR')) {
          try {
            await sendScreenView({
              flow: 'app_validation',
              step: 'user_is_blocked',
              screen_name: Routes.UNAVAILABLE_FEATURE,
              scenario: JSON.stringify({ userAccountType: organization }),
            })
          } catch (error: unknown) {
            logBridgeError('send user blocked screen view', error)
          }

          navigate(Routes.UNAVAILABLE_FEATURE)
          return
        }
      }

      // const entities = getUserEntityKeys(userEntitiesPayload)

      if (entities) {
        if (entities.includes('US_BRANCH')) {
          dispatch(
            appContextReducerActions.setUserData({
              userCountry: userEntitiesPayload.userOrganization,
              userLanguage: language,
              userAccountType: organization,
              userEntities: entities,
              entitiesOrganizations: userEntitiesPayload,
            }),
          )

          dispatch(appContextReducerActions.setEntityMenuContent(entitiesMenuInfo))
          navigate(Routes.ENTITY)
        } else {
          dispatch(appContextReducerActions.setQuickLoad(true))

          dispatch(
            appContextReducerActions.setUserData({
              userCountry: userEntitiesPayload.userOrganization,
              userLanguage: language,
              userAccountType: organization,
              userEntities: entities,
              entitiesOrganizations: userEntitiesPayload,
            }),
          )
          dispatch(appContextReducerActions.setUserSelectedEntity('BR'))

          const { privacyCenterComponents, privacyCenterEndpoints } = await getPrivacyCenter({
            userAccountType: organization,
            userCountry: setUserCountry(organization),
            userLanguage: language,
            userSelectedEntity: 'BR',
          })

          dispatch(
            appContextReducerActions.setPrivacyCenterContent({
              components: privacyCenterComponents.components,
              endpoints: privacyCenterEndpoints,
              isActive: privacyCenterComponents.isActive,
              entity: 'BR',
            }),
          )

          navigate(Routes.HOME, { state: { previousRoute: Routes.ROOT } })
        }
      } else {
        try {
          await sendError({
            flow: 'app_initialization',
            step: 'initialize_app_context',
            screen_name: Routes.ROOT,
            error_description: 'User has no entity.',
            error_message: JSON.stringify({
              error: 'Request return no entity for this user.',
              userEntitiesPayload,
              entitiesMenuInfo,
            }),
          })
        } catch (analyticsError: unknown) {
          logBridgeError('send initialize app context error analytics', analyticsError)
        }

        setIsLoading(false)
        navigate(Routes.ERROR)
      }
    } catch (error: unknown) {
      try {
        await sendError({
          flow: 'app_initialization',
          step: 'initialize_app_context',
          screen_name: Routes.ROOT,
          error_description: 'Failed to initialize user data',
          error_message: error instanceof Error ? error.message : 'Unknown initialization error',
        })
      } catch (analyticsError: unknown) {
        logBridgeError('send initialize app context error analytics', analyticsError)
      }

      dispatch(appContextReducerActions.setError(true))

      setIsLoading(false)
      navigate(Routes.ERROR)
    } finally {
      dispatch(appContextReducerActions.setLoading(false))
      setIsLoading(false)
      setIsCompleted(true)
    }
  }, [dispatch, navigate])

  useEffect(() => {
    if (appContext.isAppError) return
    if (hasInitializedContext) {
      setIsLoading(false)
      setIsCompleted(true)
      return
    }

    if (isCompleted) setIsCompleted(false)

    initializeUserData()
  }, [appContext.isAppError, hasInitializedContext, initializeUserData, isCompleted])

  return { isLoading }
}

export { useInitializeAppContext }
